import { Box } from '@mui/material'
import React, { useCallback, useReducer, useState } from 'react'
import CreateInsurancePolicyDialog from './createInsurancePolicyDialog';
import { CustomerService } from '../../../../service/customerService';
import { isLocalDev } from '../../../../utils/environment';
import useAbortController from '../../../../hooks/useAbortController';
import EnhancedTable from '../../../enhancedTable/EnhancedTable';
import { insurancePolicyListColumns } from './columns';
import ConfirmationDialog from '../../../ui/confirmationDialog';
import useDialogModal from '../../../../hooks/useDialogModal';
import validLookup from '../../../../utils/validLookup';
import { toast } from 'react-toastify';
import EditInsurancePolicyDialog from './editInsurancePolicyDialog';
import EditIcon from '@mui/icons-material/Edit';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useCan } from '../../../../hooks/useCan';

function InsurancePolicy({ customer }) {
  const can = useCan();
  const [signal, abortRequest] = useAbortController();
  // State to manage the Policy Selection Dialog
  const [openPolicyDialog, setOpenPolicyDialog] = useState(false);
  const [InsurancePolicyEditDialog, showInsurancePolicyEditForm] = useDialogModal(EditInsurancePolicyDialog);

  const [state, updateState] = useReducer((state, params) => {
    return { ...state, ...params };
  }, {
    data: [],
    totalRowCount: 0,
    pageCount: 0,
    filter: '',
    loading: false,
  });
  const [
    ConfirmationDialogComp,
    showConfirmationDialog,
    closeConfirmationDialog,
  ] = useDialogModal(ConfirmationDialog);

  const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
    const from = pageSize * pageIndex;
    updateState({ loading: true });
    CustomerService.getInsurancePolicies(from, pageSize, filters, sortBy, customer.customer.id, signal).then(result => {
      const { code, data } = result;
      if (code === 0) {
        updateState({
          data: data.data,
          totalRowCount: data.count,
          pageCount: Math.ceil(data.count / pageSize),
        });
      }
      updateState({ loading: false });
    }).catch(error => {
      if (isLocalDev) {
        console.error(error)
      }
      updateState({ loading: false });
    });
  }, []);

  const deleteRecordHandler = (rowids) => {
    showConfirmationDialog(async (action) => {
      if (action === validLookup.confirmationDialogAction.ok) {
        const deleteRecordResult = await CustomerService.deleteInsurancePolicy(customer.customer.id, rowids.map(x => parseInt(x, 10)));
        const { code, message, data } = deleteRecordResult;

        if (code === 0) {
          toast.success(message);
          const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
          updateState({ data: updateList });
        } else {
          toast.success("Failed to delete record(s)");
        }
      }
      closeConfirmationDialog();
    }, {
      title: 'Delete record',
      message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
    });
  }

  const handleRowEdit = (rowData) => {
    showInsurancePolicyEditForm(null, { recordToEdit: rowData });
  }

  // this function is called when the user clicks on the apply policy to family icon
  // this will ask user for confirmation and then apply the policy to the family members
  const handleApplyPolicyToFamily = (rowData) => {
    showConfirmationDialog(async (action) => {

      if (action === validLookup.confirmationDialogAction.ok) {
        const applyPolicyResult = await CustomerService.applyInsurancePolicyToFamily(customer.customer.id, rowData.id);
        const { code, message } = applyPolicyResult;

        if (code === 0) {
          toast.success(message);
        } else {
          toast.error("Failed to apply policy to family");
        }
      }
      closeConfirmationDialog();
    }, {
      title: 'Apply policy to family',
      message: `Please confirm if you would like to apply this policy to family members?`,
    });
  }

  const recordEditHandler = (record) => {
    // find the employee by row id
    const existingValidSupplier = state.data;
    const idx = existingValidSupplier.findIndex(e => e.id === record.id);
    const updatesList = existingValidSupplier.map((row, index) => {
      if (row.id === record.id) {
        return {
          ...existingValidSupplier[idx], ...record,
        }
      }
      return row
    });

    // update list
    updateState({
      data: updatesList
    });
  }

  return (
    <Box display="flex" flexDirection="column">
      <EnhancedTable
        columns={insurancePolicyListColumns}
        addRecordHandler={can.action('insurance', 'add-insurance') ? () => setOpenPolicyDialog(true) : undefined}
        deleteRecordHandler={can.action('insurance', 'delete-insurance') ? deleteRecordHandler : null}
        data={state.data}
        totalRowCount={state.totalRowCount}
        pageCount={state.pageCount}
        fetchData={fetchData}
        loading={state.loading}
        selectionMode="multi"
        actions={[
          // Edit Policy - requires insurance.edit-insurance permission
          ...(can.action('insurance', 'edit-insurance') ? [{
            icon: <EditIcon />,
            onClick: handleRowEdit
          }] : []),
          // Apply to Family - requires insurance.edit-insurance permission
          ...(can.action('insurance', 'edit-insurance') ? [{
            icon: <GroupAddIcon />,
            onClick: handleApplyPolicyToFamily,
            tooltip: "Apply policy to family",
            disabled: !customer.customer.familyGroupId,
          }] : [])
        ]}
      />
      <CreateInsurancePolicyDialog customer={customer} open={openPolicyDialog} onClose={() => setOpenPolicyDialog(false)} />
      <InsurancePolicyEditDialog customer={customer} onRecordEdit={recordEditHandler} />

      <ConfirmationDialogComp
        label={{
          positive: "Confirm",
          negative: "Cancel",
        }} />
    </Box>
  )
}

export default InsurancePolicy